/* global bocAmchart4, chartLanguage */
/**
 * @typedef {{chart, data, dimension, getDimension, valetGroup, valetSeries, valetObservations}} bocDataProcessor
 */

/**
 * Set the the list of series and labels from the shortcode and add all the graphs to the chart
 * Also sets the valet_url based on the series list
 *
 * @param {AMConfig} chart Chart Object to be used to build this chart
 * @param {object} data Array of series to be added
 * @param {string} dataType type of data "date" | "category"
 * @returns {bocDataProcessor} return object for further use
 */
bocAmchart4.DataProcessor = function (chart, data, dataType) {

    this.chart = chart;
    this.data = data;
    this.dimension = false;

    this.dataType = dataType;

    this.chartType = 'xy';
    if (chart.bocSettings.hasOwnProperty('type')) {
        this.chartType = chart.bocSettings.type;
    }

    this.xAxisType = "";
    if (chart.bocSettings.args.hasOwnProperty('xaxistype')) {
        this.xAxisType = chart.bocSettings.args.xaxistype;
    }

    // If it's xy chart, default to date axis
    if (this.chartType === 'xy' && this.xAxisType === "") {
        this.xAxisType = 'date';
    }

    this.graphs = new bocAmchart4.DataGraphs();

    // store the data processor for reuse
    this.chart.bocSettings.dataProcessor = this;

    if (data.hasOwnProperty('seriesDetail')) {
        this.valetSeries = data.seriesDetail;
    }

    if (data.hasOwnProperty('groupDetail')) {
        this.valetGroup = data.groupDetail;
    }

    if (data.hasOwnProperty('observations')) {
        this.valetObservations = data.observations;
    }

    if (bocAmchart4.debug(chart)) {
        // eslint-disable-next-line no-console
        console.log(
            chart.bocSettings.id,
            "Before setValetDataLoader:",
            bocAmchart4.debugClone(this)
        );
    }

    /**
     * Process all the series in valet response and try to add them to array before instantiating all graphs
     * series that are manually added by this.addGraphsFromShortcode earlier will be skipped
     * @returns {void}
     */
    this.addGraphsFromValetSeries = function () {
        var argValues = Object.values(chart.bocSettings.args);
        if ((!this.chart.hasOwnProperty("series") || this.chart.series.length === 0)
            && this.data.hasOwnProperty("seriesDetail")) {
            for (var prop in data.seriesDetail) {
                // If reserveAll isn't true, then add all series as usual
                if (!chart.bocSettings.args.hasOwnProperty('reserveall')) {
                    this.graphs.addSeriesGraph(this.xAxisType, prop, data.seriesDetail[prop].label);
                } else {
                    // reserveAll is true so add series not used as values in bocSettings.args
                    if (argValues.includes(prop)) {
                        this.graphs.addSeriesGraph(this.xAxisType, prop, data.seriesDetail[prop].label);
                    } else {
                        this.graphs.addReservedSeries(prop);
                    }
                }
            }
        }
    };

    /**
     * Process special cases where fields/series names are assigned to specific graphs numbers
     * such as data-series0valueField, data-series0openField, data-series0closeField
     * @returns {void}
     */
    this.addGraphsFromShortcode = function () {
        var chart = this.chart;
        var label, index;

        // need separate loop to reserve the series to allow valet requests by series later
        for (index = 0; index < 100; index++) {
            if (chart.bocSettings.args.hasOwnProperty('reservedseries' + index)) {
                var reservedSeries = chart.bocSettings.args['reservedseries' + index];
                this.graphs.addReservedSeries(reservedSeries);
            }

            ['trend', 'errormin', 'errormax', 'label'].forEach((name) => {
                if (chart.bocSettings.args.hasOwnProperty('series' + index + name + 'field')) {
                    var fieldSeries = chart.bocSettings.args['series' + index + name + 'field'];
                    this.graphs.addReservedSeries(fieldSeries);
                }
            });

            if (chart.bocSettings.args.hasOwnProperty('series' + index + 'xfield')) {
                var xFieldSeries = chart.bocSettings.args['series' + index + 'xfield'];
                this.graphs.addXFieldSeries(xFieldSeries, index);
            }

            if (chart.bocSettings.args.hasOwnProperty('calculatedseries' + index)) {
                var calculatedSeries = chart.bocSettings.args['calculatedseries' + index];
                label = calculatedSeries;
                this.graphs.addManualChart(this.xAxisType, calculatedSeries, label, index);
            }
        }

        for (index = 0; index < 100; index++) {
            if (chart.bocSettings.args.hasOwnProperty('series' + index + 'openfield')
                && chart.bocSettings.args.hasOwnProperty('series' + index + 'closefield')
            ) {
                var openSeries = chart.bocSettings.args['series' + index + 'openfield'];
                var closeSeries = chart.bocSettings.args['series' + index + 'closefield'];
                label = openSeries;
                if (this.valetSeries.hasOwnProperty(openSeries)) {
                    label = this.valetSeries[openSeries].label;
                }
                this.graphs.addCandlestickChart(this.xAxisType, openSeries, closeSeries, label, index);
            } else if (chart.bocSettings.args.hasOwnProperty('series' + index + 'valuefield')) {
                // this might be needed when using groups to rearrange the order of series
                var valueSeries = chart.bocSettings.args['series' + index + 'valuefield'];
                label = valueSeries;
                if (this.valetSeries.hasOwnProperty(valueSeries)) {
                    label = this.valetSeries[valueSeries].label;
                }
                this.graphs.addManualChart(this.xAxisType, valueSeries, label, index);
            } else if (chart.bocSettings.args.hasOwnProperty('series' + index)) {
                var series = chart.bocSettings.args['series' + index];
                label = series;
                if (this.valetSeries.hasOwnProperty(series)) {
                    label = this.valetSeries[series].label;
                }
                if (!chart.bocSettings.args.hasOwnProperty('xaxistype')
                    || chart.bocSettings.args['xaxistype'] !== 'text') {
                    this.graphs.addManualChart(this.xAxisType, series, label, index);
                }
            }
        }
    };

    /**
     * Process series as transposed data and adds them to the graph
     *
     * @returns {Array} Array of transposed data
     */
    this.createTransposedSeriesAndGetData = function () {
        const transposedObservations = this.getDataByDateAsSeries();

        this.addGraphsFromShortcode();
        this.addGraphsFromValetSeries();

        for (const graph of this.graphs.graphs) {
            graph["graphType"] = "line";
        }

        return transposedObservations;
    };

    /**
     * Returns true only if all series labels contain underscore
     * @returns {bool} true if all labels contain underscore
     */
    this.doLabelsContainUnderscores = function () {
        for (let series in this.valetSeries) {
            if (this.valetSeries.hasOwnProperty(series)) {
                if (!this.valetSeries[series].label.includes('_')) {
                    return false;
                }
            }
        }
        return true;
    };

    /**
     * Looks up the terms based on the data contained in series label
     *
     * @returns {void}
     */
    this.parseSeriesTermsFromLabels = function () {
        var terms,
            termsLookup,
            lookupTerm0 = false,
            lookupTerm1 = false;

        var categoryTerm = 'category';
        if (this.chart.bocSettings.type === 'map') {
            categoryTerm = 'id';
        }
        if (chart.bocSettings.args.hasOwnProperty('categoryterm')) {
            categoryTerm = chart.bocSettings.args.categoryterm;
        }

        var seriesTerm = 'series';
        if (chart.bocSettings.args.hasOwnProperty('seriesterm')) {
            seriesTerm = chart.bocSettings.args.seriesterm;
        }

        // This part to process old series when abbreviations were used instead of full labels
        if (typeof this.chart.bocSettings.terms === 'object'
            && typeof this.chart.bocSettings.terms[chartLanguage] === 'object') {
            termsLookup = chart.bocSettings.terms[chartLanguage];
            if (termsLookup.hasOwnProperty(categoryTerm)) {
                lookupTerm0 = true;
            }
            if (termsLookup.hasOwnProperty(seriesTerm)) {
                lookupTerm1 = true;
            }
        }
        for (var series in this.valetSeries) {
            if (this.valetSeries.hasOwnProperty(series)) {
                // This was done under assumption we will use .terms to store more info,
                // did not happen and probably will not, but need to keep back compatibility
                if (typeof this.valetSeries[series].terms !== 'object') {
                    // Assuming the label will contain EBPUS_3month type of data,
                    // where first is category term id and second is series term id
                    terms = this.valetSeries[series].label.split('_');
                    this.valetSeries[series].terms = {};
                    if (this.chart.bocSettings.args.hasOwnProperty('transpose')
                        && this.chart.bocSettings.args.transpose === "true") {
                        var temp = terms[1];
                        terms[1] = terms[0];
                        terms[0] = temp;
                    }

                    if (terms.length === 1) {
                        terms[1] = seriesTerm;
                    }

                    // Define the terms from terms provided or use just ids found from split
                    if (lookupTerm0 && termsLookup[categoryTerm].hasOwnProperty(terms[0])) {
                        this.valetSeries[series].terms[categoryTerm] = termsLookup[categoryTerm][terms[0]];
                    } else {
                        this.valetSeries[series].terms[categoryTerm] = terms[0];
                        // Add space after numbers to prevent bad sorting when combining numbers and text
                        // Not using for text labels to prevent breaking existing text category guides
                        if (!isNaN(terms[0])) {
                            this.valetSeries[series].terms[categoryTerm] += " ";
                        }
                    }

                    // Define the terms from terms provided or use just ids found from split
                    if (lookupTerm1 && termsLookup[seriesTerm].hasOwnProperty(terms[1])) {
                        this.valetSeries[series].terms[seriesTerm] = termsLookup[seriesTerm][terms[1]];
                    } else {
                        this.valetSeries[series].terms[seriesTerm] = terms[1];
                    }

                    if (typeof terms[2] !== "undefined") {
                        this.valetSeries[series].terms['term2'] = terms[2];
                    }
                    if (typeof terms[3] !== "undefined") {
                        this.valetSeries[series].terms['term3'] = terms[3];
                    }
                }
            }
        }
        this.categoryTerm = categoryTerm;
        this.seriesTerm = seriesTerm;
    };

    /**
     * Trigger adding the graphs to chart
     * @returns {void}
     */
    this.addGraphsToChart = function () {
        this.graphs.addGraphsToChart(this.chart);
    };

    /**
     * Get all the observations by date for date category chart
     * @returns {[]} return all observations array
     */
    this.getObservationsByDate = function () {
        var output = [];
        var revisedText = bocAmchart4.getRevisedText();
        var chart = this.chart;

        this.valetObservations.forEach(function (set) {
            var dateSet = [];
            for (var property in set) {
                if (set.hasOwnProperty(property)) {
                    if (property === 'd') {
                        dateSet.date = set.d;
                    } else {
                        dateSet[property] = set[property].v;
                        if (set[property].hasOwnProperty('r') && set[property].r === 1) {
                            dateSet[property + '_R'] = revisedText;
                        }
                        bocAmchart4.initChartVars(chart);
                        bocAmchart4.vars[chart.bocSettings.id].dashed = bocAmchart4.vars[chart.bocSettings.id].dashed || {};

                        if (bocAmchart4.vars[chart.bocSettings.id].dashed.hasOwnProperty(property)) {
                            var dashed = bocAmchart4.vars[chart.bocSettings.id].dashed[property];

                            var dashLength = 6;
                            if (dashed.hasOwnProperty('length')) {
                                // Technically value can be not integer, e.g. "6,4,2,4"
                                dashLength = dashed.length;
                            }

                            if (dashed.hasOwnProperty('start') && dashed.hasOwnProperty('end')) {
                                if (dateSet.date >= dashed.start && dateSet.date <= dashed.end) {
                                    dateSet[property + "_dashLength"] = dashLength;
                                } else {
                                    dateSet[property + "_dashLength"] = 0;
                                }
                            } else {
                                if (dashed.hasOwnProperty('start')) {
                                    if (dateSet.date >= dashed.start) {
                                        dateSet[property + "_dashLength"] = dashLength;
                                    } else {
                                        dateSet[property + "_dashLength"] = 0;
                                    }
                                }
                            }
                        }
                    }
                }
            }
            output.push(dateSet);
        });
        return output;
    };

    this.getObservationsInTwoDimensions = function () {
        let output = [];
        let seriesDetails = this.valetSeries;

        // Get all keys except for date
        let keys = Object.keys(this.valetObservations[0]);
        let index = keys.indexOf('d');
        if (index > -1) {
            keys.splice(index, 1);
        }

        var minValue = this.valetObservations[0][keys[0]].v;
        var maxValue = this.valetObservations[0][keys[0]].v;

        this.valetObservations.forEach(function (set) {
            let dateSet = [];
            for (let series in seriesDetails) {
                if (seriesDetails.hasOwnProperty(series)) {
                    let datapoint = [];
                    let category = seriesDetails[series].label;
                    datapoint['date'] = set.d;
                    datapoint['category'] = category;
                    if (set.hasOwnProperty(series)) {
                        datapoint['v'] = set[series].v;
                        minValue = datapoint['v'] < minValue ? datapoint['v'] : minValue;
                        maxValue = datapoint['v'] > maxValue ? datapoint['v'] : maxValue;
                    }
                    dateSet.push(datapoint);
                }
            }

            dateSet.forEach(function (set) {
                output.push(set);
            });
        });

        bocAmchart4.vars[chart.bocSettings.id].minValue = minValue;
        bocAmchart4.vars[chart.bocSettings.id].maxValue = maxValue;

        return output;
    };

    /**
     * Return all the observations for text category chart
     * @returns {[]} array containing all the observations
     */
    this.getObservationsByCategory = function () {
        var output = [];
        var categorySet = {};
        var seriesSet = [];
        var revisedText = bocAmchart4.getRevisedText();
        var dimension = this.dimension;
        var seriesDetails = this.valetSeries;
        var categoryTerm = 'category';
        if (this.chart.bocSettings.type === 'map') {
            categoryTerm = 'id';
        }

        // Get all keys except for "k"
        var keys = Object.keys(this.valetObservations[0]);
        var index = keys.indexOf('k');
        if (index > -1) {
            keys.splice(index, 1);
        }
        var minValue = this.valetObservations[0][keys[0]].v;
        var maxValue = this.valetObservations[0][keys[0]].v;

        const args = this.chart.bocSettings.args;

        if (args.hasOwnProperty('categoryterm')) {
            categoryTerm = args.categoryterm;
        }

        if (args.hasOwnProperty('xaxistype')
            && args.xaxistype === 'time') {
            categoryTerm = 'date';
        }

        this.valetObservations.forEach(function (obs) {
            if (dimension && obs.hasOwnProperty(dimension)) {
                let category = obs[dimension];
                // Set the category values based on dimension, make sure the value is string
                if (args.hasOwnProperty('xaxistype')
                    && args.xaxistype === 'time') {
                    // Check if category is time string in format of hh:mm:ss.mmmmmm
                    if (obs[dimension].includes(':')) {
                        category = bocAmchart4.convertTimeStringToSeconds(obs[dimension]);
                    }
                } else if (!isNaN(category) && categoryTerm === 'category') {
                    category += " ";
                }

                // Looping through series, and find an observation for each of them
                for (var series in seriesDetails) {
                    if (seriesDetails.hasOwnProperty(series)) {
                        if (obs.hasOwnProperty(series)) {
                            // Initialize the container for values of series for specific category
                            if (!categorySet.hasOwnProperty(category)) {
                                categorySet[category] = [];
                            }

                            // This value point belongs to series that has chosen dimension
                            if (seriesSet.indexOf(series) === -1) {
                                seriesSet.push(series);
                            }

                            categorySet[category][series] = obs[series].v;
                            minValue = obs[series].v < minValue ? obs[series].v : minValue;
                            maxValue = obs[series].v > maxValue ? obs[series].v : maxValue;

                            if (obs[series].hasOwnProperty('r') && obs[series].r === 1) {
                                categorySet[category][series + '_R'] = revisedText;
                            }
                        }
                    }
                }
            }
        });

        for (var category in categorySet) {
            if (categorySet.hasOwnProperty(category)) {
                categorySet[category][categoryTerm] = category
                    .replace("[", "[[")
                    .replace("]", "]]")
                ;
                output.push(Object.assign({}, categorySet[category]));
            }
        }
        bocAmchart4.vars[chart.bocSettings.id].minValue = minValue;
        bocAmchart4.vars[chart.bocSettings.id].maxValue = maxValue;
        return output;
    };

    /**
     * Return all the observations for text category chart
     * @returns {[]} array containing all the observations
     */
    this.getSeriesAndObservationsByCategoryFromDate = function () {
        var output = [];
        var categorySet = [];
        var seriesSet = [];
        var revisedText = bocAmchart4.getRevisedText();
        var dimension = this.dimension;
        var seriesDetails = this.valetSeries;
        var categoryTerm = this.categoryTerm;
        var seriesTerm = this.seriesTerm;
        var graphs = this.graphs;

        // Get all keys except for date
        var keys = Object.keys(this.valetObservations[0]);
        var index = keys.indexOf('d');
        if (index > -1) {
            keys.splice(index, 1);
        }
        var minValue = this.valetObservations[0][keys[0]].v;
        var maxValue = this.valetObservations[0][keys[0]].v;

        this.valetObservations.forEach(function (obs) {
            if (dimension && obs.hasOwnProperty(dimension)) {
                for (var series in seriesDetails) {
                    // property is either date or series name, ignoring the date field for now
                    if (seriesDetails.hasOwnProperty(series) && obs.hasOwnProperty(series)) {
                        var oneSeriesDetails = seriesDetails[series];
                        if (oneSeriesDetails.hasOwnProperty('terms')) {
                            var terms = oneSeriesDetails.terms;
                            if (terms.hasOwnProperty(categoryTerm) && terms.hasOwnProperty(seriesTerm)) {
                                // This value point belongs to series that has category term defined
                                if (!categorySet.hasOwnProperty(terms[categoryTerm])) {
                                    categorySet[terms[categoryTerm]] = {};
                                }

                                // This value point belongs to series that has category term defined
                                if (seriesSet.indexOf(terms[seriesTerm]) === -1) {
                                    seriesSet.push(terms[seriesTerm]);
                                    graphs.addSeriesGraph('text', terms[seriesTerm], terms[seriesTerm]);
                                }

                                categorySet[terms[categoryTerm]][terms[seriesTerm]] = obs[series].v;

                                minValue = obs[series].v < minValue ? obs[series].v : minValue;
                                maxValue = obs[series].v > maxValue ? obs[series].v : maxValue;

                                if (obs[series].hasOwnProperty('r') && obs[series].r === 1) {
                                    categorySet[terms[categoryTerm]][terms[seriesTerm] + '_R'] = revisedText;
                                }
                                if (terms.hasOwnProperty('term2')) {
                                    categorySet[terms[categoryTerm]][terms[seriesTerm] + '_term2'] = terms['term2'];
                                }
                                if (terms.hasOwnProperty('term3')) {
                                    categorySet[terms[categoryTerm]][terms[seriesTerm] + '_term3'] = terms['term3'];
                                }
                            }
                        }
                    }
                }
            }
        });
        for (var category in categorySet) {
            if (categorySet.hasOwnProperty(category)) {
                if (chart.bocSettings.type !== 'map') {
                    categoryTerm = 'category';
                }
                categorySet[category][categoryTerm] = category
                    .replace("[", "[[")
                    .replace("]", "]]")
                ;
                output.push(categorySet[category]);
            }
        }
        bocAmchart4.vars[chart.bocSettings.id].minValue = minValue;
        bocAmchart4.vars[chart.bocSettings.id].maxValue = maxValue;
        return output;
    };

    /**
     * If a category has an observation date earlier than a category listed previous, then that category will be
     * displayed first. This will correct the category order to match that of the series group
     * @param {array} data processed data
     * @returns {*[]} array with the categories ordered the same as in the valet group
     */
    this.orderCategoriesFromDateByGroupOrder = function (data) {
        let valetSeries = this.chart.bocSettings.dataProcessor.valetSeries;
        let orderCategories = [];
        let tempData = [];

        for (let seriesDetail in valetSeries) {
            let category = valetSeries[seriesDetail].description.split("_")[0];
            if (!orderCategories.includes(category)) {
                orderCategories.push(category);

                data.forEach(function (dataValue) {
                    if (dataValue.category === category) {
                        tempData.push(dataValue);
                    }
                });
            }
        }
        return tempData;
    };

    /**
     * Return all the observations for text category chart based on specific date
     * @param {string} specificDate date for which to extract the observations
     * @returns {[]} array containing one date observation set
     */
    this.getSeriesAndObservationsByCategoryByDate = function (specificDate) {
        var output = [];
        var categorySet = [];
        var revisedText = bocAmchart4.getRevisedText();
        var dimension = this.dimension;
        var seriesDetails = this.valetSeries;
        var categoryTerm = this.categoryTerm;
        var seriesTerm = this.seriesTerm;

        this.valetObservations.forEach(function (obs) {
            if (dimension && obs.hasOwnProperty(dimension)) {
                if (obs[dimension] !== specificDate) {
                    return;
                }
                for (var series in seriesDetails) {
                    // property is either date or series name, ignoring the date field for now
                    if (seriesDetails.hasOwnProperty(series) && obs.hasOwnProperty(series)) {
                        var oneSeriesDetails = seriesDetails[series];
                        if (oneSeriesDetails.hasOwnProperty('terms')) {
                            var terms = oneSeriesDetails.terms;
                            if (terms.hasOwnProperty(categoryTerm) && terms.hasOwnProperty(seriesTerm)) {
                                // This value point belongs to series that has category term defined
                                if (!categorySet.hasOwnProperty(terms[categoryTerm])) {
                                    categorySet[terms[categoryTerm]] = {};
                                }

                                categorySet[terms[categoryTerm]][terms[seriesTerm]] = obs[series].v;
                                if (obs[series].hasOwnProperty('r') && obs[series].r === 1) {
                                    categorySet[terms[categoryTerm]][terms[seriesTerm] + '_R'] = revisedText;
                                }
                                if (terms.hasOwnProperty('term2')) {
                                    categorySet[terms[categoryTerm]][terms[seriesTerm] + '_term2'] = terms['term2'];
                                }
                                if (terms.hasOwnProperty('term3')) {
                                    categorySet[terms[categoryTerm]][terms[seriesTerm] + '_term3'] = terms['term3'];
                                }
                            }
                        }
                    }
                }
            }
        });

        for (var category in categorySet) {
            if (categorySet.hasOwnProperty(category)) {
                categorySet[category][categoryTerm] = category;
                output.push(categorySet[category]);
            }
        }

        return output;
    };

    /**
     * Get dimension from valet responce or false if there's no dimensions found
     *
     * @returns {boolean|string} dimension used in this valet response
     */
    this.getDimension = function () {
        if (this.dimension) {
            return this.dimension;
        }

        var dimensions = [];
        var dimension = false;

        for (var series in this.valetSeries) {
            if (this.valetSeries.hasOwnProperty(series)) {
                var seriesDetails = this.valetSeries[series];
                if (seriesDetails.hasOwnProperty("dimension") && seriesDetails.dimension.hasOwnProperty("key")) {
                    if (dimensions.indexOf(seriesDetails.dimension.key) === -1) {
                        dimensions.push(seriesDetails.dimension.key);
                    }
                }
            }
        }

        if (dimensions.length > 0) {
            dimension = dimensions[0];
            if (dimensions.length > 1) {
                // eslint-disable-next-line no-console
                console.warn("Data error, more than one dimension found in one data set.");
            }
            this.dimension = dimension;
        } else {
            this.dimension = 'none';
        }

        return this.dimension;
    };

    /**
     * Return an array of restructured transposed data
     * @returns {Array} Array of transposed data
     */
    this.getDataByDateAsSeries = function () {
        const observationsBySeries = {};
        const newSeriesDetail = {};

        this.data.observations.forEach(set => {
            let date = set.d;
            if (chart.bocSettings.args.hasOwnProperty("period")) {
                const dateObject = new Date(date + "T00:00");
                if (chart.bocSettings.args["period"] === "quarter") {
                    const quarterIdentifier = chartLanguage === "fr" ? "T" : "Q";
                    date = dateObject.getFullYear() + quarterIdentifier + Math.ceil((dateObject.getMonth() + 1) / 3);
                } else if (chart.bocSettings.args["period"] === "year") {
                    date = dateObject.getFullYear() + "";
                } else if (chart.bocSettings.args["period"] === "month") {
                    const locale = chartLanguage === "fr" ? "fr-CA" : "en-CA";
                    date = dateObject.toLocaleString(locale, {month: 'short'}) + " " + dateObject.getFullYear();
                }
            }
            newSeriesDetail[date] = {'label': date};

            for (const property in set) {
                if (property === 'd') {
                    continue;
                }

                const value = set[property].v;

                if (!observationsBySeries.hasOwnProperty(property)) {
                    let label = this.data.seriesDetail[property].label;
                    //if the chart is a pie chart

                    if (chart.bocSettings.hasOwnProperty('type') && chart.bocSettings.type === 'pie') {
                        //split the label by the character '_'
                        const labelStrings = this.data.seriesDetail[property].label.split('_');
                        //take the [1]th instance of the label after the split
                        label = labelStrings[1];
                        //assign this to the label
                    }
                    observationsBySeries[property] = {'category': label};
                }
                observationsBySeries[property][date] = value;
            }
        });

        this.data.seriesDetail = newSeriesDetail;
        return Object.values(observationsBySeries);
    };
};
